import axios from '@/libs/axios'

const BASE_URL = 'https://mc-analytics.manyconvert.com'

/**
 * Cria uma nova campanha de aniversário com envio condicional de templates
 * 
 * IMPORTANTE: Esta campanha envia DOIS TEMPLATES diferentes baseado em CONDIÇÃO
 * - Template 1: Para clientes que compraram nos últimos purchase_days dias (compradores recentes)
 * - Template 2: Para clientes que compraram há mais de purchase_days dias (compradores inativos)
 * - O backend verifica automaticamente a condição e envia o template correspondente
 * 
 * @param {Object} dados - Dados da campanha
 * @param {string} dados.name - Nome da campanha
 * @param {string} dados.template_name_recent - Nome do template para compradores recentes
 * @param {string} dados.template_name_inactive - Nome do template para compradores inativos
 * @param {number} [dados.purchase_days=15] - Dias para considerar comprador recente (enviado como recent_purchase_days)
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object>} Campanha criada
 */
export async function criarCampanhaAniversario(dados, storeUuid) {
  try {
    // Normalizar send_time para HH:MM ou HH:MM:SS
    const sendTime = dados.birthday_send_time || dados.send_time || '10:00'

    const payload = {
      store_uuid: storeUuid,
      name: dados.name || 'Campanha de Aniversário',
      template_name_recent: dados.template_name_recent,
      template_name_inactive: dados.template_name_inactive,
      send_time: sendTime,
      daily_limit: dados.birthday_daily_limit || dados.daily_limit || 1000,
      recent_purchase_days: dados.purchase_days != null ? Number(dados.purchase_days) : 15
    }

    const response = await axios.post(`${BASE_URL}/api/birthday-campaigns`, payload)
    return response.data.data || response.data
  } catch (error) {
    console.error('Erro ao criar campanha de aniversário:', error)
    throw error
  }
}

/**
 * Ativa uma campanha de aniversário
 * 
 * Configurações de ENVIO (não de recomendação):
 * - send_time: Horário fixo diário para envio
 * - daily_limit: Limite diário de disparos
 * 
 * @param {number} campaignId - ID da campanha
 * @param {Object} config - Configurações de envio
 * @param {string} config.birthday_send_time - Horário de envio (HH:MM:SS)
 * @param {number} config.birthday_daily_limit - Limite diário de envios
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object>} Campanha ativada
 */
export async function ativarCampanhaAniversario(campaignId, config, storeUuid) {
  try {
    const payload = {
      send_time: config.birthday_send_time,
      daily_limit: config.birthday_daily_limit
    }

    const response = await axios.post(
      `${BASE_URL}/api/birthday-campaigns/${campaignId}/activate`,
      payload,
      {
        params: { store_uuid: storeUuid }
      }
    )
    
    return response.data.data || response.data
  } catch (error) {
    console.error('Erro ao ativar campanha de aniversário:', error)
    throw error
  }
}

/**
 * Desativa uma campanha de aniversário
 * @param {number} campaignId - ID da campanha
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object>} Resultado da desativação
 */
export async function desativarCampanhaAniversario(campaignId, storeUuid) {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/birthday-campaigns/${campaignId}/deactivate`,
      {},
      {
        params: { store_uuid: storeUuid }
      }
    )
    
    return response.data.data || response.data
  } catch (error) {
    console.error('Erro ao desativar campanha de aniversário:', error)
    throw error
  }
}

/**
 * Busca status e métricas de uma campanha de aniversário
 * @param {number} campaignId - ID da campanha
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object>} Status e métricas da campanha
 */
export async function buscarStatusCampanhaAniversario(campaignId, storeUuid) {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/birthday-campaigns/${campaignId}/status`,
      {
        params: { store_uuid: storeUuid }
      }
    )
    
    return response.data.data || response.data
  } catch (error) {
    console.error('Erro ao buscar status da campanha de aniversário:', error)
    throw error
  }
}

/**
 * Lista todas as campanhas de aniversário de uma loja
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Array>} Lista de campanhas de aniversário
 */
export async function listarCampanhasAniversario(storeUuid) {
  try {
    const response = await axios.get(`${BASE_URL}/api/birthday-campaigns`, {
      params: { store_uuid: storeUuid }
    })
    
    return response.data.data || response.data || []
  } catch (error) {
    console.error('Erro ao listar campanhas de aniversário:', error)
    throw error
  }
}

/**
 * Busca campanha de aniversário ativa para uma loja
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object|null>} Campanha de aniversário ativa ou null
 */
export async function buscarCampanhaAniversarioAtiva(storeUuid) {
  try {
    const campanhas = await listarCampanhasAniversario(storeUuid)
    
    // Filtrar apenas campanhas de aniversário ativas
    const campanhaAniversario = campanhas.find(c => c.is_active === 1)
    
    return campanhaAniversario || null
  } catch (error) {
    console.error('Erro ao buscar campanha de aniversário ativa:', error)
    return null
  }
}

/**
 * Upload de mídia (imagem ou vídeo) para o header do template da campanha de aniversário
 * NOVO: Suporta upload para templates condicionais (recent ou inactive)
 * 
 * @param {number} campaignId - ID da campanha (deve estar inativa)
 * @param {File} file - Arquivo de imagem (JPG, PNG, etc.) ou vídeo (MP4, etc.)
 * @param {string} storeUuid - UUID da loja
 * @param {string} templateType - Tipo do template: 'recent' (compradores recentes) ou 'inactive' (compradores inativos)
 * @returns {Promise<Object>} Resultado do upload
 */
export async function uploadMediaCampanhaAniversario(campaignId, file, storeUuid, templateType = 'recent') {
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('template_type', templateType) // 'recent' ou 'inactive'

    const response = await axios.post(
      `${BASE_URL}/api/birthday-campaigns/${campaignId}/upload-media`,
      formData,
      {
        params: { store_uuid: storeUuid },
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    return response.data.data || response.data
  } catch (error) {
    console.error('Erro ao fazer upload de mídia da campanha de aniversário:', error)
    throw error
  }
}

/**
 * Envia mensagem de teste de aniversário para um lead específico
 * @param {number} campaignId - ID da campanha (deve estar ativa)
 * @param {string} name - Nome do lead
 * @param {string} phone - Telefone do lead
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object>} Resultado do envio
 */
export async function enviarTesteBirthdayMessage(campaignId, name, phone, storeUuid) {
  try {
    const payload = {
      campaign_id: campaignId,
      name,
      phone
    }

    const response = await axios.post(
      `${BASE_URL}/api/birthday-campaigns/test-send`,
      payload,
      {
        params: { store_uuid: storeUuid }
      }
    )
    
    return response.data
  } catch (error) {
    console.error('Erro ao enviar mensagem de teste:', error)
    throw error
  }
}

/**
 * Busca métricas de conversão da campanha de aniversário com janela de atribuição
 * @param {number} campaignId - ID da campanha
 * @param {string} storeUuid - UUID da loja
 * @param {number} attributionWindowHours - Janela de atribuição em horas (padrão: 168 = 7 dias)
 * @returns {Promise<Object>} Métricas de conversão
 */
export async function buscarMetricasConversaoAniversario(campaignId, storeUuid, attributionWindowHours = 168) {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/birthday-campaigns/${campaignId}/conversions`,
      {
        params: { 
          store_uuid: storeUuid,
          attribution_window_hours: attributionWindowHours
        }
      }
    )
    
    return response.data.data || response.data
  } catch (error) {
    console.error('Erro ao buscar métricas de conversão da campanha de aniversário:', error)
    throw error
  }
}

/**
 * Busca pedidos/conversões da campanha de aniversário
 * @param {number} campaignId - ID da campanha
 * @param {string} storeUuid - UUID da loja
 * @param {number} attributionWindowHours - Janela de atribuição em horas
 * @returns {Promise<Array>} Lista de pedidos/conversões
 */
export async function buscarPedidosAniversario(campaignId, storeUuid, attributionWindowHours = 168) {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/birthday-campaigns/${campaignId}/orders`,
      {
        params: { 
          store_uuid: storeUuid,
          attribution_window_hours: attributionWindowHours
        }
      }
    )
    
    return response.data.data || response.data || []
  } catch (error) {
    console.error('Erro ao buscar pedidos da campanha de aniversário:', error)
    throw error
  }
}

/**
 * Busca mensagens enviadas da campanha de aniversário
 * @param {number} campaignId - ID da campanha
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Array>} Lista de mensagens enviadas formatadas
 */
export async function buscarMensagensEnviadasAniversario(campaignId, storeUuid) {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/birthday-campaigns/${campaignId}/sent-messages`,
      {
        params: { store_uuid: storeUuid }
      }
    )
    
    const data = response.data.data
    
    // Se não houver dados, retornar array vazio
    if (!data || !data.messages) {
      return []
    }
    
    // Transformar a estrutura do backend para o formato esperado pelo frontend
    const mensagensFormatadas = data.messages.map(msg => {
      // Determinar o tipo de template baseado no nome
      // Se tiver template_name_2 e o template usado é diferente do template principal, é inativo
      const isInactiveTemplate = data.campaign.template_name_2 && 
                                  msg.template_name === data.campaign.template_name_2
      
      return {
        lead_id: msg.lead?.id || null,
        customer_name: msg.lead?.name || 'N/A',
        customer_phone: msg.lead?.phone || 'N/A',
        customer_email: msg.lead?.email || null,
        birthday: msg.lead?.birthday || null,
        template_name: msg.template_name,
        template_type: isInactiveTemplate ? 'inactive' : 'recent',
        status: msg.status,
        sent_at: msg.timestamps?.sent_at || msg.timestamps?.scheduled_at,
        scheduled_at: msg.timestamps?.scheduled_at,
        failed_at: msg.timestamps?.failed_at,
        error_message: msg.error_message,
        provider_message_id: msg.provider_message_id,
        // Dados de compra do cliente
        total_purchases: msg.lead?.purchase_stats?.total_pedidos || 0,
        total_spent: msg.lead?.purchase_stats?.valor_total || 0,
        average_ticket: msg.lead?.purchase_stats?.ticket_medio || 0,
        last_purchase_date: msg.lead?.purchase_stats?.ultima_compra || null,
        first_purchase_date: msg.lead?.purchase_stats?.primeira_compra || null
      }
    })
    
    return mensagensFormatadas
  } catch (error) {
    console.error('Erro ao buscar mensagens enviadas da campanha de aniversário:', error)
    throw error
  }
}

/**
 * Busca informações detalhadas de um cliente da campanha de aniversário
 * @param {number} campaignId - ID da campanha
 * @param {string} storeUuid - UUID da loja
 * @param {string} leadId - ID do lead/cliente
 * @returns {Promise<Object>} Informações detalhadas do cliente
 */
export async function buscarInfoClienteAniversario(campaignId, storeUuid, leadId) {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/birthday-campaigns/${campaignId}/customer/${leadId}`,
      {
        params: { store_uuid: storeUuid }
      }
    )
    
    return response.data.data || response.data
  } catch (error) {
    console.error('Erro ao buscar informações do cliente:', error)
    throw error
  }
}
